import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * RateCardsPage — P1-05 round 2.
 *
 * Operator surface for the metering pricing matrix
 * (`/v1/admin/metering/rate-cards`, see P1-NF01).
 *
 * Layout:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Filter bar (app / kind / provider / model / retired) + "New" │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │ Table: App | Kind | Provider | Model | Price | … | Actions    │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Editing happens in a Bootstrap modal — both "New" and "Edit" reuse the
 * same form component. Wildcard apps (appId === null) are surfaced as a
 * "Wildcard" checkbox that disables the App field; this matches the
 * server's `IS NOT DISTINCT FROM` collision semantics where wildcard +
 * concrete share a row identity.
 *
 * Retired vs active filter: server query `?active=true` returns
 * `effectiveTo IS NULL` only. "Show retired" toggle flips this; when on,
 * we omit the param and the server returns the full history including
 * superseded rows. Delete is soft (sets `effectiveTo = now()`); the row
 * stays visible in retired mode for forensic purposes.
 *
 * 409 RATE_CARD_EXISTS (older builds: RATE_CARD_COLLISION) surfaces as an inline red banner inside the
 * modal — caller is expected to retire the existing active row first.
 *
 * NEXUS-1455: a card is identified by (app, kind, model, provider) since
 * migration 050, so the table shows Provider and Model (NULL = "Any") —
 * without them the per-provider wildcards looked like duplicates. Token
 * kinds show their price per million tokens; the old 6-dp per-token
 * rounding turned $0.50/MTok into "$0.00". Model and provider are set on
 * create only (the server's PATCH does not change them).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
/** Provider kinds the server accepts (ChatProviderKind), with operator-facing names. */
export const PROVIDER_KINDS = [
    ['ollama', 'Ollama (Nexus AI hosts)'],
    ['openai', 'OpenAI-compatible (incl. Nexus AI relay)'],
    ['anthropic', 'Anthropic'],
    ['gemini', 'Google Gemini'],
    ['bedrock', 'AWS Bedrock'],
    ['cli', 'CLI'],
    ['agent_control', 'Agent control'],
];
export function providerLabel(kind) {
    if (!kind)
        return 'Any';
    return PROVIDER_KINDS.find(([k]) => k === kind)?.[1] ?? kind;
}
function todayIso() {
    // YYYY-MM-DD in local tz — what <input type="date"> emits/expects.
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function emptyDraft() {
    return {
        appId: '',
        wildcard: false,
        kind: '',
        model: '',
        providerKind: '',
        unitCost: '',
        currency: 'USD',
        effectiveFrom: todayIso(),
    };
}
function cardToDraft(c) {
    // effectiveFrom on the wire is an ISO timestamp; trim to YYYY-MM-DD
    // for the date input. A bad/missing value just falls back to today.
    const fromDate = c.effectiveFrom ? c.effectiveFrom.slice(0, 10) : todayIso();
    return {
        appId: c.appId ?? '',
        wildcard: c.appId === null,
        kind: c.kind,
        model: c.model ?? '',
        providerKind: c.providerKind ?? '',
        unitCost: plainDecimal(c.unitCost),
        currency: c.currency,
        effectiveFrom: fromDate,
    };
}
export function draftToCreate(d) {
    const out = {
        appId: d.wildcard ? null : d.appId.trim(),
        kind: d.kind.trim(),
        model: d.model.trim() || null,
        providerKind: d.providerKind || null,
        unitCost: Number(d.unitCost),
        currency: d.currency.trim().toUpperCase(),
    };
    if (d.effectiveFrom.trim())
        out.effectiveFrom = d.effectiveFrom;
    return out;
}
/**
 * Build the PATCH diff against the original row. Only fields that
 * actually changed go on the wire — including the wildcard sentinel
 * (we always send appId when wildcard toggled or text changed, so the
 * server's `'appId' in body` check fires and accepts the null promotion).
 */
function draftToPatch(d, original) {
    const patch = {};
    const newAppId = d.wildcard ? null : d.appId.trim();
    if (newAppId !== original.appId)
        patch.appId = newAppId;
    const newKind = d.kind.trim();
    if (newKind !== original.kind)
        patch.kind = newKind;
    const newCost = Number(d.unitCost);
    if (Number.isFinite(newCost) && newCost !== original.unitCost) {
        patch.unitCost = newCost;
    }
    const newCcy = d.currency.trim().toUpperCase();
    if (newCcy !== original.currency)
        patch.currency = newCcy;
    const originalFromDate = original.effectiveFrom?.slice(0, 10) ?? '';
    if (d.effectiveFrom && d.effectiveFrom !== originalFromDate) {
        patch.effectiveFrom = d.effectiveFrom;
    }
    return patch;
}
function errorFieldKey(err) {
    const raw = err.path ?? err.field ?? '';
    const segs = raw.replace(/^\/+/, '').split(/[./]/).filter(Boolean);
    const last = segs[segs.length - 1];
    switch (last) {
        case 'appId':
            return 'appId';
        case 'kind':
            return 'kind';
        case 'model':
            return 'model';
        case 'providerKind':
            return 'providerKind';
        case 'unitCost':
            return 'unitCost';
        case 'currency':
            return 'currency';
        case 'effectiveFrom':
            return 'effectiveFrom';
        default:
            return null;
    }
}
function formatDate(iso) {
    if (!iso)
        return '—';
    // Trim time portion for display; we don't need second-level precision
    // on the operator surface.
    return iso.slice(0, 10);
}
/** ai.tokens.in / .out / .thinking … are priced per token and read per million tokens. */
export function isTokenKind(kind) {
    return kind.startsWith('ai.tokens.');
}
/** 0.0000005 → "0.0000005" (String() would give "5e-7" in the edit form). */
export function plainDecimal(n) {
    return n.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 });
}
function money(n, currency, maxDp) {
    const v = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: maxDp });
    return currency === 'USD' ? `$${v}` : `${v} ${currency}`;
}
/**
 * Display price. Token kinds: per million tokens ("$0.50 / M tokens") — the
 * per-token figure is too small to read and 6-dp rounding showed $0.50/MTok
 * as $0.00. Other kinds: the exact per-unit cost (no lossy rounding).
 */
export function formatUnitCost(cost, currency, kind = '') {
    if (isTokenKind(kind)) {
        const perM = cost * 1e6;
        // Keep a tiny non-zero price from reading as $0.00.
        return `${money(perM, currency, perM > 0 && perM < 0.00005 ? 12 : 4)} / M tokens`;
    }
    return money(cost, currency, 12);
}
/** Exact per-unit value for the tooltip. */
function exactUnitCost(cost, currency, kind) {
    return `${money(cost, currency, 12)} per ${isTokenKind(kind) ? 'token' : 'unit'}`;
}
function describeCard(c) {
    return [
        c.appId ?? 'all apps',
        c.kind,
        c.providerKind ? providerLabel(c.providerKind) : 'any provider',
        c.model ?? 'any model',
    ].join(' / ');
}
/** null (wildcard) sorts first so the general card heads its group. */
function cmpNullable(a, b) {
    if ((a ?? null) === (b ?? null))
        return 0;
    if (a == null)
        return -1;
    if (b == null)
        return 1;
    return a.localeCompare(b);
}
const WILDCARD_FILTER = '__wildcard__';
const ALL_APPS_FILTER = '__all__';
const ALL_PROVIDERS_FILTER = '__all__';
const ANY_PROVIDER_FILTER = '__any__';
// ---------------------------------------------------------------------
// Component
export function RateCardsPage() {
    const fetcher = useNexusFetch();
    // ---- Filters ------------------------------------------------------
    const [appFilter, setAppFilter] = useState(ALL_APPS_FILTER);
    const [kindFilter, setKindFilter] = useState('');
    const [providerFilter, setProviderFilter] = useState(ALL_PROVIDERS_FILTER);
    const [modelFilter, setModelFilter] = useState('');
    const [showRetired, setShowRetired] = useState(false);
    // ---- Data ---------------------------------------------------------
    const [cards, setCards] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [actionBanner, setActionBanner] = useState(null);
    const [modal, setModal] = useState({ kind: 'closed' });
    const [draft, setDraft] = useState(emptyDraft);
    const [submitting, setSubmitting] = useState(false);
    const [formBanner, setFormBanner] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalErrors, setGeneralErrors] = useState([]);
    // ---- Load ---------------------------------------------------------
    const loadCards = useCallback(async () => {
        setLoadError(null);
        try {
            // We always fetch unscoped by app/kind from the server and apply
            // those filters client-side so the dropdown options stay stable
            // as the user narrows the view. `active=true` IS server-side
            // since it materially changes what rows are returned.
            const qs = showRetired ? '' : '?active=true';
            const res = await fetcher(`/v1/admin/metering/rate-cards${qs}`);
            setCards(res?.rateCards ?? []);
        }
        catch (e) {
            setCards([]);
            setLoadError(e instanceof Error ? e.message : 'failed to load rate cards');
        }
    }, [fetcher, showRetired]);
    useEffect(() => {
        void loadCards();
    }, [loadCards]);
    // ---- Derived ------------------------------------------------------
    const distinctAppIds = useMemo(() => {
        if (!cards)
            return [];
        const set = new Set();
        for (const c of cards) {
            if (c.appId !== null)
                set.add(c.appId);
        }
        return Array.from(set).sort();
    }, [cards]);
    const distinctProviders = useMemo(() => {
        const set = new Set(PROVIDER_KINDS.map(([k]) => k));
        for (const c of cards ?? [])
            if (c.providerKind)
                set.add(c.providerKind);
        return Array.from(set);
    }, [cards]);
    const filteredCards = useMemo(() => {
        if (!cards)
            return [];
        const kindNeedle = kindFilter.trim().toLowerCase();
        const modelNeedle = modelFilter.trim().toLowerCase();
        return cards
            .filter((c) => {
            if (appFilter === WILDCARD_FILTER) {
                if (c.appId !== null)
                    return false;
            }
            else if (appFilter !== ALL_APPS_FILTER) {
                if (c.appId !== appFilter)
                    return false;
            }
            if (kindNeedle && !c.kind.toLowerCase().includes(kindNeedle))
                return false;
            if (providerFilter === ANY_PROVIDER_FILTER) {
                if (c.providerKind)
                    return false;
            }
            else if (providerFilter !== ALL_PROVIDERS_FILTER) {
                if (c.providerKind !== providerFilter)
                    return false;
            }
            if (modelNeedle && !(c.model ?? '').toLowerCase().includes(modelNeedle))
                return false;
            return true;
        })
            .sort((a, b) => a.kind.localeCompare(b.kind) ||
            cmpNullable(a.providerKind, b.providerKind) ||
            cmpNullable(a.model, b.model) ||
            cmpNullable(a.appId, b.appId) ||
            b.effectiveFrom.localeCompare(a.effectiveFrom));
    }, [cards, appFilter, kindFilter, providerFilter, modelFilter]);
    // ---- Modal handlers ----------------------------------------------
    const openCreate = useCallback(() => {
        setDraft(emptyDraft());
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        setModal({ kind: 'create' });
    }, []);
    const openEdit = useCallback((row) => {
        setDraft(cardToDraft(row));
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        setModal({ kind: 'edit', row });
    }, []);
    const closeModal = useCallback(() => {
        if (submitting)
            return;
        setModal({ kind: 'closed' });
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
    }, [submitting]);
    const updateField = useCallback((key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setFieldErrors((fe) => {
            if (!fe[key])
                return fe;
            const next = { ...fe };
            delete next[key];
            return next;
        });
    }, []);
    const applyServerErrors = useCallback((err) => {
        if (err.code === 'RATE_CARD_EXISTS' || err.code === 'RATE_CARD_COLLISION') {
            setFormBanner('An active rate card for this app, kind, provider and model already exists. ' +
                'Retire it first, or edit it.');
            return;
        }
        if (err.code === 'INVALID_REQUEST' && Array.isArray(err.errors)) {
            const byField = {};
            const general = [];
            for (const e of err.errors) {
                const key = errorFieldKey(e);
                if (key) {
                    (byField[key] ??= []).push(e.message);
                }
                else {
                    general.push(e.message);
                }
            }
            setFieldErrors(byField);
            setGeneralErrors(general);
            setFormBanner(err.message || 'rate card shape failed validation');
            return;
        }
        setFormBanner(`${err.code}: ${err.message}`);
    }, []);
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (modal.kind === 'closed')
            return;
        setSubmitting(true);
        setFormBanner(null);
        setFieldErrors({});
        setGeneralErrors([]);
        try {
            if (modal.kind === 'create') {
                await fetcher('/v1/admin/metering/rate-cards', {
                    method: 'POST',
                    json: draftToCreate(draft),
                });
            }
            else {
                const patch = draftToPatch(draft, modal.row);
                if (Object.keys(patch).length === 0) {
                    // No-op edit — close cleanly without burning a request.
                    setModal({ kind: 'closed' });
                    return;
                }
                await fetcher(`/v1/admin/metering/rate-cards/${encodeURIComponent(modal.row.id)}`, { method: 'PATCH', json: patch });
            }
            setModal({ kind: 'closed' });
            setActionBanner(null);
            await loadCards();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                applyServerErrors(err);
            }
            else {
                setFormBanner(err instanceof Error ? err.message : 'request failed (unknown error)');
            }
        }
        finally {
            setSubmitting(false);
        }
    }, [applyServerErrors, draft, fetcher, loadCards, modal]);
    const handleDelete = useCallback(async (row) => {
        const label = describeCard(row);
        if (!window.confirm(`Retire rate card "${label}"? This sets effectiveTo = now() — ` +
            `the row stays visible under "Show retired" but is no longer ` +
            `used for new metering events.`)) {
            return;
        }
        setActionBanner(null);
        try {
            await fetcher(`/v1/admin/metering/rate-cards/${encodeURIComponent(row.id)}`, { method: 'DELETE' });
            await loadCards();
        }
        catch (err) {
            if (err instanceof NexusAdminApiError) {
                setActionBanner(`${err.code}: ${err.message}`);
            }
            else {
                setActionBanner(err instanceof Error ? err.message : 'delete failed');
            }
        }
    }, [fetcher, loadCards]);
    // ---- Render -------------------------------------------------------
    const totalCount = cards?.length ?? 0;
    const shownCount = filteredCards.length;
    return (_jsxs("div", { children: [_jsx("h1", { className: "h3 mb-3", children: "Rate cards" }), _jsxs("p", { className: "text-muted small mb-4", children: ["Pricing matrix for metered usage. Each row prices a metered", ' ', _jsx("strong", { children: "kind" }), ", optionally narrowed to an app, a provider and a model; an empty one (", _jsx("strong", { children: "All apps" }), " / ", _jsx("strong", { children: "Any" }), ") is a wildcard. When several active cards match an event the most specific wins: app, then model, then provider. Token kinds are shown per million tokens. Retiring a row soft-deletes it (sets", ' ', _jsx("code", { children: "effectiveTo = now()" }), ") so historical metering events retain their original price."] }), _jsx("div", { className: "card mb-3", children: _jsx("div", { className: "card-body", children: _jsxs("div", { className: "row g-2 align-items-end", children: [_jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label small mb-1", children: "App" }), _jsxs("select", { className: "form-select form-select-sm", value: appFilter, onChange: (e) => setAppFilter(e.target.value), disabled: cards === null, children: [_jsx("option", { value: ALL_APPS_FILTER, children: "All apps" }), _jsx("option", { value: WILDCARD_FILTER, children: "Wildcard only" }), distinctAppIds.map((id) => (_jsx("option", { value: id, children: id }, id)))] })] }), _jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label small mb-1", children: "Kind" }), _jsx("input", { type: "text", className: "form-control form-control-sm", placeholder: "substring match", value: kindFilter, onChange: (e) => setKindFilter(e.target.value), spellCheck: false })] }), _jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "rate-cards-provider", children: "Provider" }), _jsxs("select", { id: "rate-cards-provider", className: "form-select form-select-sm", value: providerFilter, onChange: (e) => setProviderFilter(e.target.value), disabled: cards === null, children: [_jsx("option", { value: ALL_PROVIDERS_FILTER, children: "All providers" }), _jsx("option", { value: ANY_PROVIDER_FILTER, children: "Any-provider cards only" }), distinctProviders.map((k) => (_jsx("option", { value: k, children: providerLabel(k) }, k)))] })] }), _jsxs("div", { className: "col-md-2", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "rate-cards-model", children: "Model" }), _jsx("input", { id: "rate-cards-model", type: "text", className: "form-control form-control-sm", placeholder: "substring match", value: modelFilter, onChange: (e) => setModelFilter(e.target.value), spellCheck: false })] }), _jsx("div", { className: "col-md-auto", children: _jsxs("div", { className: "form-check mt-3", children: [_jsx("input", { type: "checkbox", className: "form-check-input", id: "rate-cards-show-retired", checked: showRetired, onChange: (e) => setShowRetired(e.target.checked) }), _jsx("label", { className: "form-check-label small", htmlFor: "rate-cards-show-retired", children: "Show retired" })] }) }), _jsxs("div", { className: "col-md d-flex justify-content-end gap-2", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => void loadCards(), disabled: cards === null, children: "Refresh" }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreate, children: "New rate card" })] })] }) }) }), actionBanner && (_jsx("div", { className: "alert alert-danger", children: actionBanner })), _jsxs("div", { className: "card", children: [_jsxs("div", { className: "card-header d-flex justify-content-between align-items-center", children: [_jsx("span", { className: "fw-semibold", children: "Rate cards" }), _jsx("span", { className: "text-muted small", children: cards === null
                                    ? 'Loading…'
                                    : shownCount === totalCount
                                        ? `${totalCount} row${totalCount === 1 ? '' : 's'}`
                                        : `${shownCount} of ${totalCount} row${totalCount === 1 ? '' : 's'}` })] }), cards === null && (_jsxs("div", { className: "card-body text-muted small", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Loading rate cards\u2026"] })), cards !== null && loadError !== null && (_jsxs("div", { className: "card-body", children: [_jsx("div", { className: "alert alert-danger small mb-2", children: loadError }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void loadCards(), children: "Retry" })] })), cards !== null && loadError === null && filteredCards.length === 0 && (_jsxs("div", { className: "card-body text-center text-muted", children: [_jsx("p", { className: "mb-2", children: totalCount === 0
                                    ? 'No rate cards configured.'
                                    : 'No rate cards match the current filters.' }), totalCount === 0 && (_jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: openCreate, children: "New rate card" }))] })), cards !== null && loadError === null && filteredCards.length > 0 && (_jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-striped table-hover mb-0 align-middle", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: "App" }), _jsx("th", { scope: "col", children: "Kind" }), _jsx("th", { scope: "col", children: "Provider" }), _jsx("th", { scope: "col", children: "Model" }), _jsx("th", { scope: "col", className: "text-end", children: "Price" }), _jsx("th", { scope: "col", children: "Currency" }), _jsx("th", { scope: "col", children: "Effective from" }), _jsx("th", { scope: "col", children: "Effective to" }), _jsx("th", { scope: "col", children: "Status" }), _jsx("th", { scope: "col", className: "text-end", children: "Actions" })] }) }), _jsx("tbody", { children: filteredCards.map((c) => {
                                        const retired = c.effectiveTo !== null;
                                        return (_jsxs("tr", { children: [_jsx("td", { children: c.appId === null ? (_jsx("span", { className: "badge bg-secondary", children: "All apps" })) : (_jsx("span", { className: "badge bg-primary", children: c.appId })) }), _jsx("td", { children: _jsx("code", { children: c.kind }) }), _jsx("td", { className: c.providerKind ? undefined : 'text-muted', children: providerLabel(c.providerKind) }), _jsx("td", { className: c.model ? undefined : 'text-muted', children: c.model ? _jsx("code", { children: c.model }) : 'Any' }), _jsx("td", { className: "text-end text-nowrap", title: exactUnitCost(c.unitCost, c.currency, c.kind), children: formatUnitCost(c.unitCost, c.currency, c.kind) }), _jsx("td", { children: c.currency }), _jsx("td", { className: "small", children: formatDate(c.effectiveFrom) }), _jsx("td", { className: "small", children: formatDate(c.effectiveTo) }), _jsx("td", { children: retired ? (_jsx("span", { className: "badge bg-secondary", children: "Retired" })) : (_jsx("span", { className: "badge bg-success", children: "Active" })) }), _jsx("td", { className: "text-end", children: _jsxs("div", { className: "btn-group btn-group-sm", role: "group", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => openEdit(c), children: "Edit" }), !retired && (_jsx("button", { type: "button", className: "btn btn-outline-danger", onClick: () => void handleDelete(c), children: "Delete" }))] }) })] }, c.id));
                                    }) })] }) }))] }), modal.kind !== 'closed' && (_jsx(RateCardModal, { mode: modal.kind, draft: draft, fieldErrors: fieldErrors, generalErrors: generalErrors, formBanner: formBanner, submitting: submitting, onChange: updateField, onCancel: closeModal, onSubmit: handleSubmit }))] }));
}
function RateCardModal({ mode, draft, fieldErrors, generalErrors, formBanner, submitting, onChange, onCancel, onSubmit, }) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal show", style: { display: 'block' }, role: "dialog", "aria-modal": "true", children: _jsx("div", { className: "modal-dialog modal-dialog-centered", children: _jsx("div", { className: "modal-content", children: _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsxs("div", { className: "modal-header", children: [_jsx("h5", { className: "modal-title", children: mode === 'create' ? 'New rate card' : 'Edit rate card' }), _jsx("button", { type: "button", className: "btn-close", "aria-label": "Close", onClick: onCancel, disabled: submitting })] }), _jsxs("div", { className: "modal-body", children: [formBanner && (_jsx("div", { className: "alert alert-danger", children: formBanner })), generalErrors.length > 0 && (_jsxs("div", { className: "alert alert-warning", children: [_jsx("div", { className: "fw-semibold mb-1", children: "Server validation errors:" }), _jsx("ul", { className: "mb-0 small", children: generalErrors.map((m, i) => (_jsx("li", { children: m }, i))) })] })), _jsxs(Field, { label: "App", required: !draft.wildcard, errors: fieldErrors.appId, hint: draft.wildcard
                                                ? 'Wildcard rate card — applies to every app for this kind.'
                                                : 'The appId this rate card applies to.', children: [_jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.appId), value: draft.appId, onChange: (e) => onChange('appId', e.target.value), disabled: draft.wildcard, autoComplete: "off", spellCheck: false, placeholder: draft.wildcard ? '(wildcard)' : 'e.g. aquastar' }), _jsxs("div", { className: "form-check mt-2", children: [_jsx("input", { type: "checkbox", className: "form-check-input", id: "rate-card-modal-wildcard", checked: draft.wildcard, onChange: (e) => onChange('wildcard', e.target.checked) }), _jsx("label", { className: "form-check-label small", htmlFor: "rate-card-modal-wildcard", children: "Wildcard (applies to all apps)" })] })] }), _jsx(Field, { label: "Kind", required: true, errors: fieldErrors.kind, hint: "e.g. ai.tokens.in, ai.tokens.out, iot.telemetry_msg.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.kind), value: draft.kind, onChange: (e) => onChange('kind', e.target.value), "aria-label": "Kind", autoComplete: "off", spellCheck: false }) }), _jsx(Field, { label: "Provider", errors: fieldErrors.providerKind, hint: mode === 'edit'
                                                ? 'Fixed for an existing card — to price another provider, create a new card.'
                                                : 'Leave as Any to price every provider for this kind.', children: _jsxs("select", { className: fieldClass('form-select', fieldErrors.providerKind), value: draft.providerKind, onChange: (e) => onChange('providerKind', e.target.value), disabled: mode === 'edit', "aria-label": "Provider", children: [_jsx("option", { value: "", children: "Any provider" }), PROVIDER_KINDS.map(([k, label]) => (_jsx("option", { value: k, children: label }, k))), draft.providerKind &&
                                                        !PROVIDER_KINDS.some(([k]) => k === draft.providerKind) && (_jsx("option", { value: draft.providerKind, children: draft.providerKind }))] }) }), _jsx(Field, { label: "Model", errors: fieldErrors.model, hint: mode === 'edit'
                                                ? 'Fixed for an existing card — to price another model, create a new card.'
                                                : 'Exact model id as metered (e.g. qwen3:8b, claude-sonnet-4-5). Empty = any model.', children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.model), value: draft.model, onChange: (e) => onChange('model', e.target.value), disabled: mode === 'edit', autoComplete: "off", spellCheck: false, placeholder: "Any model", "aria-label": "Model" }) }), _jsx(Field, { label: "Unit cost", required: true, errors: fieldErrors.unitCost, hint: costHint(draft), children: _jsx("input", { type: "number", inputMode: "decimal", step: "any", min: "0", className: fieldClass('form-control', fieldErrors.unitCost), value: draft.unitCost, onChange: (e) => onChange('unitCost', e.target.value) }) }), _jsx(Field, { label: "Currency", required: true, errors: fieldErrors.currency, hint: "ISO 4217 code (e.g. USD). Coerced to uppercase on save.", children: _jsx("input", { type: "text", className: fieldClass('form-control', fieldErrors.currency), value: draft.currency, onChange: (e) => onChange('currency', e.target.value.toUpperCase().slice(0, 3)), maxLength: 3, autoComplete: "off", spellCheck: false, style: { textTransform: 'uppercase' } }) }), _jsx(Field, { label: "Effective from", required: true, errors: fieldErrors.effectiveFrom, hint: "Date this rate card starts applying. Defaults to today.", children: _jsx("input", { type: "date", className: fieldClass('form-control', fieldErrors.effectiveFrom), value: draft.effectiveFrom, onChange: (e) => onChange('effectiveFrom', e.target.value) }) })] }), _jsxs("div", { className: "modal-footer", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: onCancel, disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting, children: submitting ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Saving\u2026"] })) : mode === 'create' ? ('Create') : ('Save') })] })] }) }) }) }), _jsx("div", { className: "modal-backdrop show" })] }));
}
/** Unit-cost hint: token kinds also show the per-million figure the table uses. */
export function costHint(d) {
    const base = 'Per-unit cost in the chosen currency.';
    const n = Number(d.unitCost);
    if (!isTokenKind(d.kind.trim()))
        return base;
    if (d.unitCost.trim() === '' || !Number.isFinite(n)) {
        return `${base} Token kinds are priced per token, e.g. 0.0000005 = $0.50 per million tokens.`;
    }
    return `${base} = ${formatUnitCost(n, d.currency.trim().toUpperCase() || 'USD', d.kind.trim())}.`;
}
function fieldClass(base, errs) {
    return errs && errs.length > 0 ? `${base} is-invalid` : base;
}
function Field({ label, required, errors, hint, children }) {
    return (_jsxs("div", { className: "mb-3", children: [_jsxs("label", { className: "form-label mb-1", children: [label, required && _jsx("span", { className: "text-danger ms-1", children: "*" })] }), children, hint && !errors?.length && _jsx("div", { className: "form-text small", children: hint }), errors?.map((m, i) => (_jsx("div", { className: "invalid-feedback d-block small", children: m }, i)))] }));
}
//# sourceMappingURL=RateCardsPage.js.map